import {inject, Injectable} from '@angular/core';
import {HttpClient} from '@angular/common/http';
import {HttpErrorResponse} from '@angular/common/http';
import {Observable, of, throwError} from 'rxjs';
import {catchError, tap} from 'rxjs/operators';

import {WorkflowDefinitionControllerService} from '../api/api/workflowDefinitionController.service';
import {WorkflowDefinition} from '../api/model/workflowDefinition';

/**
 * Stage-übergreifender myCheck-/Prüflauf-Katalog – planBIC-Seite.
 *
 * <p>Bewusst handgeschrieben: die Endpunkte unter {@code /workflows/catalog} sind noch nicht im
 * generierten Client ({@code src/app/api} ist gitignored und wird erst mit laufendem Backend neu
 * erzeugt). Sobald er sie kennt, kann dieser Service auf den generierten Controller umziehen.
 *
 * <p>In Beta wird hier sofort bereitgestellt ({@link CatalogService#provide}); nach Live übernimmt
 * das Admin Center, und nur dort wird in Live freigegeben und zurückgezogen.
 */

/** Typ eines Katalog-Eintrags. KI/IDS/HYBRID = myCheck (Regelset), WORKFLOW = Prüflauf. */
export type CatalogItemType = 'KI' | 'IDS' | 'HYBRID' | 'WORKFLOW';

/**
 * OPEN/FETCHED/REJECTED stammen aus der Zeit der Einreichungen; INSTALLED ist eine Bereitstellung,
 * die in dieser Stage sofort installiert wurde (nur noch Protokoll).
 */
export type CatalogSubmissionStatus = 'OPEN' | 'FETCHED' | 'REJECTED' | 'INSTALLED';

export interface CatalogConfig {
  /** Darf aus dieser Stage eingereicht werden? (Backend: {@code bic.catalog.submission-stages}) */
  submissionAllowed: boolean;
  stage: string;
}

export interface CatalogSubmission {
  id: string;
  catalogKey: string;
  itemType: CatalogItemType;
  sourceId: string;
  /**
   * Nur bei einer Komponenten-Einreichung gesetzt: ID des myCheck-Entwurfs, aus dem die neue
   * Prüflauf-Version entstand. {@code itemType}/{@code sourceId} zeigen dann auf den Prüflauf.
   */
  componentSourceId: string | null;
  title: string;
  changelog: string;
  submittedByName: string;
  submittedAt: string;
  status: CatalogSubmissionStatus;
  rejectReason: string | null;
}

/** Zielgruppe einer Bereitstellung – dieselbe Form wie im Backend ({@code CatalogDtos.Targets}). */
export interface CatalogTargets {
  global: boolean;
  organisationIds: number[];
  userIds: number[];
}

export interface CatalogProvideRequest {
  itemType: CatalogItemType;
  sourceId: string;
  projectId?: number;
  changelog: string;
  /** {@code null} = Zielgruppe der bestehenden Bereitstellung behalten. */
  targets: CatalogTargets | null;
}

/** Installation in dieser Stage, wie {@code POST /workflows/catalog/provide} sie zurückgibt. */
export interface CatalogInstallation {
  catalogKey: string;
  itemType: CatalogItemType;
  /** Revision in dieser Stage – zählt mit jeder Bereitstellung hoch. */
  version: number;
  installedAt: string | null;
  installedBy: string | null;
  revokedAt: string | null;
  targets: CatalogTargets;
}

/** Organisation im Ziel-Picker. */
export interface CatalogTargetOrganisation {
  id: number;
  name: string;
}

/** Über die E-Mail-Adresse aufgelöster Nutzer im Ziel-Picker. */
export interface CatalogTargetUser {
  id: number;
  mail: string;
  displayName: string;
}

export type CatalogFindingSeverity = 'BLOCKER' | 'WARNING';

export interface CatalogFinding {
  severity: CatalogFindingSeverity;
  code: string;
  message: string;
  ref?: string | null;
}

/** Katalog-Eintrag, in den eine Einreichung ginge. */
export interface CatalogSubmitTarget {
  catalogKey: string;
  itemType: CatalogItemType;
  title: string;
}

export interface CatalogPreflightResult {
  /** {@code true} = keine BLOCKER. */
  ok: boolean;
  findings: CatalogFinding[];
  /**
   * Katalog-Einträge, in die eingereicht würde: normal genau einer; bei einem myCheck, der nur
   * als Teil zentraler Prüfläufe existiert, der/die Prüfläufe (WORKFLOW); leer, wenn die Quelle
   * noch nicht im Katalog ist.
   */
  submitsAs: CatalogSubmitTarget[];
}

/**
 * Katalog-Felder der Definition, die der generierte {@link WorkflowDefinition} noch nicht kennt.
 * Gesetzt auf einem Duplikat einer Katalog-Kopie.
 */
export type WorkflowDefinitionWithCatalog = WorkflowDefinition & {
  sourceCatalogKey?: string | null;
  sourceCatalogVersion?: number | null;
};

/** Schlüssel für die Zuordnung Einreichung → Karte. */
export function catalogSourceKey(itemType: CatalogItemType, sourceId: string): string {
  return `${itemType}:${sourceId}`;
}

/**
 * Schlüssel für die Karte eines enthaltenen myChecks: seine Einreichung ist eine des Prüflaufs
 * (itemType WORKFLOW), die Karte findet sie nur über {@code componentSourceId}.
 */
export function catalogComponentKey(sourceId: string): string {
  return `COMPONENT:${sourceId}`;
}

/**
 * Letzte Einreichung je Quelle (itemType+sourceId). Eine neuere Einreichung derselben Quelle
 * überdeckt ältere – auf der Karte zählt nur der aktuelle Stand.
 */
export function latestSubmissionBySource(submissions: CatalogSubmission[]): Map<string, CatalogSubmission> {
  const result = new Map<string, CatalogSubmission>();
  for (const submission of submissions) {
    rememberLatestSubmission(result, submission);
  }
  return result;
}

/**
 * Trägt eine Einreichung unter ihrer Quelle ein, sofern sie neuer ist – bei einem enthaltenen
 * myCheck zusätzlich unter {@link catalogComponentKey}.
 */
export function rememberLatestSubmission(target: Map<string, CatalogSubmission>, submission: CatalogSubmission): void {
  const keys = [catalogSourceKey(submission.itemType, submission.sourceId)];
  if (submission.componentSourceId) {
    keys.push(catalogComponentKey(submission.componentSourceId));
  }
  for (const key of keys) {
    const current = target.get(key);
    if (!current || new Date(submission.submittedAt).getTime() >= new Date(current.submittedAt).getTime()) {
      target.set(key, submission);
    }
  }
}

/** Anzeigetext des Status. OPEN und FETCHED sind für den Einreicher gleich: „liegt vor". */
export function catalogSubmissionStatusLabel(status: CatalogSubmissionStatus): string {
  switch (status) {
    case 'INSTALLED':
      return 'Zentral bereitgestellt';
    case 'REJECTED':
      return 'Abgelehnt';
    default:
      return 'Eingereicht';
  }
}

@Injectable({providedIn: 'root'})
export class CatalogService {
  private http = inject(HttpClient);
  private definitionApi = inject(WorkflowDefinitionControllerService);
  /** Nur ein erfolgreiches Ergebnis wird gemerkt – ein Fehler soll beim nächsten Mal erneut laden. */
  private cachedConfig: CatalogConfig | null = null;

  /** Dieselbe Basis wie der generierte Client (vgl. {@code WorkflowService.basePath}). */
  private get basePath(): string {
    return (this.definitionApi as any).basePath ?? '';
  }

  private get catalogPath(): string {
    return `${this.basePath}/workflows/catalog`;
  }

  /** Einmal pro Sitzung geladen; die Stage-Konfiguration ändert sich nur mit einem Deployment. */
  getConfig(): Observable<CatalogConfig> {
    if (this.cachedConfig) return of(this.cachedConfig);
    return this.http.get<CatalogConfig>(`${this.catalogPath}/config`).pipe(
      tap(config => (this.cachedConfig = config)),
    );
  }

  /**
   * Ohne Filter: alle eigenen Einreichungen. Mit beiden Filtern: die einer Quelle – bei einem
   * myCheck auch die Prüflauf-Einreichungen, die aus seinem Entwurf entstanden
   * ({@code componentSourceId}).
   */
  listSubmissions(filter?: { itemType: CatalogItemType; sourceId: string }): Observable<CatalogSubmission[]> {
    const params = filter ? {params: {itemType: filter.itemType, sourceId: filter.sourceId}} : {};
    return this.http.get<CatalogSubmission[]>(`${this.catalogPath}/submissions`, params);
  }

  /**
   * In dieser Stage (beta) sofort bereitstellen. Antwortet mit einer Liste: ein enthaltener
   * myCheck installiert je Prüflauf, zu dem er gehört, eine neue Revision.
   */
  provide(request: CatalogProvideRequest): Observable<CatalogInstallation[]> {
    return this.http.post<CatalogInstallation[]>(`${this.catalogPath}/provide`, request);
  }

  private get sharesPath(): string {
    return `${this.basePath}/workflows/definitions/shares`;
  }

  /** Organisationen für den Ziel-Picker. */
  lookupOrganisations(query?: string): Observable<CatalogTargetOrganisation[]> {
    const params = query ? {params: {query}} : {};
    return this.http.get<CatalogTargetOrganisation[]>(`${this.sharesPath}/lookup/organisations`, params);
  }

  /** Einen Nutzer über seine E-Mail-Adresse auflösen; {@code null}, wenn es keinen gibt (404). */
  lookupUser(mail: string): Observable<CatalogTargetUser | null> {
    return this.http.get<CatalogTargetUser>(`${this.sharesPath}/lookup/users`, {params: {mail}}).pipe(
      catchError((err: HttpErrorResponse) => err.status === 404 ? of(null) : throwError(() => err)),
    );
  }

  /** Vorprüfung gegen die eigene Stage – schreibt nichts. */
  preflight(itemType: CatalogItemType, sourceId: string, projectId?: number): Observable<CatalogPreflightResult> {
    const params: Record<string, string | number> = {itemType, sourceId};
    if (projectId != null) params['projectId'] = projectId;
    return this.http.get<CatalogPreflightResult>(`${this.catalogPath}/preflight`, {params});
  }
}
